import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type {
  AgentWorkbenchPatch,
  AgentWorkSurfaceManifest,
  ContentRun,
  ContentPiece,
} from "../../../shared/types";

// ── Queries ──

export function useContentRuns() {
  return useQuery({
    queryKey: ["content-runs"],
    queryFn: () => api.get<{ data: ContentRun[] }>("/api/content/runs"),
  });
}

export function useContentRun(runId: string | undefined) {
  return useQuery({
    queryKey: ["content-runs", runId],
    queryFn: () =>
      api.get<{ data: ContentRun }>(`/api/content/runs/${runId}`),
    enabled: !!runId,
    // Poll every 3s while pipeline is processing
    refetchInterval: (query) => {
      const run = query.state.data?.data;
      if (!run) return false;
      const statuses = Object.values(run.agent_status);
      const isProcessing = statuses.some(
        (s) => s === "queued" || s === "working",
      );
      return isProcessing ? 3_000 : false;
    },
  });
}

export function useContentPieces(runId: string | undefined) {
  return useQuery({
    queryKey: ["content-runs", runId, "pieces"],
    queryFn: () =>
      api.get<{ data: ContentPiece[] }>(
        `/api/content/runs/${runId}/pieces`,
      ),
    enabled: !!runId,
  });
}

export function useContentPieceWorkbench(pieceId: string | undefined) {
  return useQuery({
    queryKey: ["content-pieces", pieceId, "workbench"],
    queryFn: () =>
      api.get<{ data: AgentWorkSurfaceManifest }>(
        `/api/agent-workbench/draft/${pieceId}`,
      ),
    enabled: !!pieceId,
    // Manifest only changes via the apply mutation (which invalidates this query),
    // so avoid refetching on every window focus/remount while the editor is open.
    staleTime: 60_000,
  });
}

// ── Mutations ──

export function useStartPipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      brand_id: string;
      checkin_id?: string;
      text?: string;
    }) => api.post<{ data: ContentRun }>("/api/content/run", payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["content-runs"] });
    },
  });
}

export function useSelectIdea(runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { selected_idea_index: number }) =>
      api.post<{ data: ContentRun }>(
        `/api/content/runs/${runId}/select`,
        payload,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["content-runs", runId] });
      qc.invalidateQueries({ queryKey: ["content-runs"] });
    },
  });
}

export function useUpdatePiece() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { id: string; run_id?: string; data: Partial<ContentPiece> }) =>
      api.put<{ data: ContentPiece }>(
        `/api/content/pieces/${payload.id}`,
        payload.data,
      ),
    onSuccess: (_data, variables) => {
      if (variables.run_id) {
        qc.invalidateQueries({ queryKey: ["content-runs", variables.run_id] });
        qc.invalidateQueries({ queryKey: ["content-runs", variables.run_id, "pieces"] });
      }
      // Invalidate pieces list for any run that contains this piece
      qc.invalidateQueries({ queryKey: ["content-runs"] });
      // FR-13: an edit resets status to draft — refresh the workspace KPI + summary.
      qc.invalidateQueries({ queryKey: ["brand-stats"] });
      qc.invalidateQueries({ queryKey: ["content-summary"] });
    },
  });
}

export function useProposeContentPieceWorkbenchPatch(pieceId: string) {
  return useMutation({
    mutationFn: (body: {
      target_section_id?: string | null;
      quick_action_id?: string | null;
      instruction?: string | null;
    }) =>
      api.post<{ data: AgentWorkbenchPatch }>(
        `/api/agent-workbench/draft/${pieceId}/propose`,
        body,
      ),
  });
}

export function useApplyContentPieceWorkbenchPatch(pieceId: string, runId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: AgentWorkbenchPatch) =>
      api.post<{ data: AgentWorkSurfaceManifest }>(
        `/api/agent-workbench/draft/${pieceId}/apply`,
        { patch },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["content-pieces", pieceId, "workbench"] });
      if (runId) {
        qc.invalidateQueries({ queryKey: ["content-runs", runId] });
        qc.invalidateQueries({ queryKey: ["content-runs", runId, "pieces"] });
      }
      qc.invalidateQueries({ queryKey: ["content-runs"] });
    },
  });
}
